import { useEffect, useState } from "react";
import { adminDeleteInquiry, adminFetchInquiries, adminUpdateInquiry } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = {
    new: "border-orange-400/40 text-orange-300 bg-orange-400/10",
    contacted: "border-blue-400/40 text-blue-300 bg-blue-400/10",
    confirmed: "border-green-400/40 text-green-300 bg-green-400/10",
    declined: "border-red-400/40 text-red-300 bg-red-400/10",
    completed: "border-white/30 text-white/70 bg-white/5",
};

export default function AdminInquiriesPage() {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("all");

    const load = async () => setItems(await adminFetchInquiries());
    useEffect(() => { load().catch(() => {}); }, []);

    const updateStatus = async (id, status) => {
        const updated = await adminUpdateInquiry(id, { status });
        setItems((p) => p.map((x) => x.id === id ? updated : x));
        toast.success("Status updated.");
    };
    const remove = async (id) => {
        if (!window.confirm("Delete this inquiry?")) return;
        await adminDeleteInquiry(id);
        setItems((p) => p.filter((x) => x.id !== id));
        toast.success("Deleted.");
    };

    const filtered = filter === "all" ? items : items.filter((i) => i.status === filter);

    return (
        <div data-testid="admin-inquiries-manager">
            <div className="flex items-end justify-between gap-3 mb-6 flex-wrap">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.32em] text-orange-300/90">Admin</div>
                    <h1 className="font-display text-3xl tracking-[0.05em] text-white">Booking Inquiries</h1>
                </div>
                <div className="min-w-44">
                    <Select value={filter} onValueChange={setFilter}>
                        <SelectTrigger className="bg-black/40 border-white/10" data-testid="admin-inquiries-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All</SelectItem>
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="contacted">Contacted</SelectItem>
                            <SelectItem value="confirmed">Confirmed</SelectItem>
                            <SelectItem value="declined">Declined</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <div className="space-y-3" data-testid="admin-inquiries-list">
                {filtered.length === 0 && (
                    <Card className="surface-card border-0"><CardContent className="p-6 text-white/65">No inquiries.</CardContent></Card>
                )}
                {filtered.map((i) => (
                    <Card key={i.id} className="surface-card border-0" data-testid="admin-inquiries-row">
                        <CardContent className="p-5">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <div className="font-display text-lg tracking-[0.05em] text-white">{i.name}</div>
                                        <Badge variant="outline" className={STATUS_BADGE[i.status] || STATUS_BADGE.new}>{i.status}</Badge>
                                        <Badge variant="outline" className="border-white/15 text-white/65 text-[10px]">{i.event_type}</Badge>
                                    </div>
                                    <div className="text-white/70 text-sm mt-1">
                                        <a className="hover:text-orange-300" href={`mailto:${i.email}`}>{i.email}</a>
                                        {i.phone ? (<> &middot; <a className="hover:text-orange-300" href={`tel:${i.phone.replace(/\D/g,'')}`}>{i.phone}</a></>) : null}
                                    </div>
                                    <div className="text-white/65 text-sm mt-1">
                                        {i.event_date && (<>{new Date(i.event_date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} · </>)}
                                        {i.guest_count ? `${i.guest_count} guests` : ""}
                                        {i.location ? ` · ${i.location}` : ""}
                                        {i.budget ? ` · ${i.budget}` : ""}
                                    </div>
                                    {i.menu_preferences && (<div className="text-white/75 text-sm mt-2"><span className="text-white/55">Menu:</span> {i.menu_preferences}</div>)}
                                    {i.notes && (<div className="text-white/75 text-sm mt-1"><span className="text-white/55">Notes:</span> {i.notes}</div>)}
                                    <div className="text-white/45 text-[11px] mt-2">Submitted {new Date(i.created_at).toLocaleString()}</div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Select value={i.status} onValueChange={(v) => updateStatus(i.id, v)}>
                                        <SelectTrigger className="bg-black/40 border-white/10 min-w-32" data-testid="admin-inquiry-status-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="new">New</SelectItem>
                                            <SelectItem value="contacted">Contacted</SelectItem>
                                            <SelectItem value="confirmed">Confirmed</SelectItem>
                                            <SelectItem value="declined">Declined</SelectItem>
                                            <SelectItem value="completed">Completed</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <button className="p-2 rounded-md border border-red-500/20 text-red-300 hover:bg-red-500/10" onClick={() => remove(i.id)} aria-label="Delete" data-testid="admin-inquiry-delete-button"><Trash2 size={16}/></button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>
        </div>
    );
}
